import { defineComponent, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { get, intoEditable, update } from '@/services/api/resources/sensor';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as fetchAllEntityItems } from '@/services/api/resources/entityItem';
import { filterListToOptions, IdProps, pushRoute, sequence } from '@/ui/helpers/uiHelper';
import { versioned } from '@/services/api';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { useSensorForm } from './useSensorForm';
import { useSensorRecord } from './useSensorRecord';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { useCreatableUserModal } from '@/ui/pages/useCreatableUserModal';

export const SensorEditPage = defineComponent<IdProps>({
  name: 'SensorEditPage',
  props: ['id'],
  setup(props) {
    if (!hasFeature('sensors')) {
      return renderResourceTeaserPage(t('common.sensors'));
    }

    if (!hasPermission(Permissions.MANAGE_HARDWARE)) {
      return renderForbiddenPage;
    }

    let recordVersion = '';
    const userOptions = ref<SelectOptions>([]);
    const entityItemOptions = ref<SelectOptions>([]);

    // create empty record
    const record = useSensorRecord();

    // get record
    const getRecord = useTask(
      () => sequence(get(props.id), getAllUsers(), fetchAllEntityItems()),
      ([{ data, version }, users, entityItems]) => {
        userOptions.value = filterListToOptions(users);
        entityItemOptions.value = filterListToOptions(entityItems);
        record.value = intoEditable(data);
        recordVersion = version;
      },
    );
    watch(ref(props.id), getRecord.run);

    // send record
    const sendRecord = useTask(
      () => update(props.id, versioned(record.value, recordVersion)),
      () => pushRoute({ name: 'sensors' }),
    );

    // form
    const { formFields, handleSubmit } = useSensorForm(record, sendRecord);

    useCreatableUserModal(userOptions, id => {
      record.value.alarmUsers.push(id);
    });

    return function render() {
      return container(
        [
          pageTitle(t('sensors.edit')),
          box([loader('form', getRecord, [
            ...formFields(userOptions.value, entityItemOptions.value),
            loadingButton(
              t('sensors.form.update-btn'),
              handleSubmit,
              sendRecord.state.value.pending,
            ),
          ]),
          ]),
        ],
        'narrow',
      );
    };
  },
});
